import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type StudentGroupItem = { id: string; name: string };

export type StudentReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  groupId?: string;
  packageType?: "package" | "private_lesson" | "all";
  groupBy?: "day" | "month";
  thresholdSessions?: number;
  thresholdHours?: number;
};

const PACKAGE_TYPE_OPTIONS = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "package", label: "ແພັກເກດຄລາດສ໌" },
  { value: "private_lesson", label: "ແພັກເກດຮຽນສ່ວນຕົວ" },
];

const GROUP_BY_OPTIONS = [
  { value: "day", label: "ຕາມວັນ" },
  { value: "month", label: "ຕາມເດືອນ" },
];

const THRESHOLD_SESSIONS_OPTIONS = [
  { value: "1", label: "< 1 ເວັ້ນໄຊຊັນ" },
  { value: "3", label: "< 3 ເວັ້ນໄຊຊັນ" },
  { value: "5", label: "< 5 ເວັ້ນໄຊຊັນ" },
  { value: "10", label: "< 10 ເວັ້ນໄຊຊັນ" },
];

const THRESHOLD_HOURS_OPTIONS = [
  { value: "1", label: "< 1 ຊົ່ວໂມງ" },
  { value: "2", label: "< 2 ຊົ່ວໂມງ" },
  { value: "5", label: "< 5 ຊົ່ວໂມງ" },
];

export function StudentReportFilter() {
  const nav = useNavigate({ from: "/app/reports/students" });
  const search = useSearch({
    from: "/app/reports/students",
  }) as StudentReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [groupId, setGroupId] = useState(search.groupId ?? "");
  const [packageType, setPackageType] = useState<
    "package" | "private_lesson" | "all"
  >(search.packageType ?? "all");
  const [groupBy, setGroupBy] = useState<"day" | "month">(
    search.groupBy ?? "day",
  );
  const [thresholdSessions, setThresholdSessions] = useState(
    String(search.thresholdSessions ?? 3),
  );
  const [thresholdHours, setThresholdHours] = useState(
    String(search.thresholdHours ?? 2),
  );

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setGroupId(search.groupId ?? "");
    setPackageType(search.packageType ?? "all");
    setGroupBy(search.groupBy ?? "day");
    setThresholdSessions(String(search.thresholdSessions ?? 3));
    setThresholdHours(String(search.thresholdHours ?? 2));
  }, [
    search.dateFrom,
    search.dateTo,
    search.groupId,
    search.packageType,
    search.groupBy,
    search.thresholdSessions,
    search.thresholdHours,
  ]);

  const hasFilter =
    !!groupId ||
    !!search.dateFrom ||
    !!search.dateTo ||
    packageType !== "all" ||
    groupBy !== "day" ||
    thresholdSessions !== "3" ||
    thresholdHours !== "2";

  const applyFilters = (updates: Partial<StudentReportSearch>) => {
    const next = {
      dateFrom: updates.dateFrom ?? fromDate,
      dateTo: updates.dateTo ?? toDate,
      groupId:
        updates.groupId !== undefined ? updates.groupId : groupId || undefined,
      packageType:
        updates.packageType !== undefined ? updates.packageType : packageType,
      groupBy: updates.groupBy !== undefined ? updates.groupBy : groupBy,
      thresholdSessions:
        updates.thresholdSessions !== undefined
          ? updates.thresholdSessions
          : Number.parseInt(thresholdSessions, 10),
      thresholdHours:
        updates.thresholdHours !== undefined
          ? updates.thresholdHours
          : Number.parseInt(thresholdHours, 10),
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <InfiniteCombobox<StudentGroupItem>
            queryKey={["master", "student-groups", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<StudentGroupItem>(
                `${config.apiUrl}/master/student-groups/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<StudentGroupItem>(
                `${config.apiUrl}/master/student-groups/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={groupId}
            onValueChange={(val) => {
              setGroupId(val ?? "");
              applyFilters({ groupId: val ?? undefined });
            }}
            placeholder="ກຸ່ມນັກຮຽນ"
            className="h-8 w-48"
            clearable
          />

          <SimpleSelect
            value={packageType}
            onValueChange={(val) => {
              const v = val as "package" | "private_lesson" | "all";
              setPackageType(v);
              applyFilters({ packageType: v });
            }}
            placeholder="ປະເພດແພັກເກດ"
            className="!h-8 w-40"
            options={PACKAGE_TYPE_OPTIONS}
          />

          <SimpleSelect
            value={groupBy}
            onValueChange={(val) => {
              const v = val as "day" | "month";
              setGroupBy(v);
              applyFilters({ groupBy: v });
            }}
            placeholder="ແນວໂນ້ມ"
            className="!h-8 w-32"
            options={GROUP_BY_OPTIONS}
          />

          <SimpleSelect
            value={thresholdSessions}
            onValueChange={(val) => {
              setThresholdSessions(val);
              applyFilters({ thresholdSessions: Number.parseInt(val, 10) });
            }}
            placeholder="ເວັ້ນໄຊຊັນ"
            className="!h-8 w-36"
            options={THRESHOLD_SESSIONS_OPTIONS}
          />

          <SimpleSelect
            value={thresholdHours}
            onValueChange={(val) => {
              setThresholdHours(val);
              applyFilters({ thresholdHours: Number.parseInt(val, 10) });
            }}
            placeholder="ຊົ່ວໂມງ"
            className="!h-8 w-32"
            options={THRESHOLD_HOURS_OPTIONS}
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              setGroupId("");
              setPackageType("all");
              setGroupBy("day");
              setThresholdSessions("3");
              setThresholdHours("2");
              nav({
                search: {
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                  groupId: undefined,
                  packageType: undefined,
                  groupBy: undefined,
                  thresholdSessions: undefined,
                  thresholdHours: undefined,
                },
              });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
